import { RawIntlProvider, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Header } from '@liveauctioneers/hammer-ui-liveauctioneers/header';
import { en } from '@liveauctioneers/hammer-ui-liveauctioneers/intl/en';
import { HeaderProps, RecentSearch, SavedSeller } from '@liveauctioneers/hammer-ui-liveauctioneers/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { logOutBidder } from '@/redux/modules/account/logout/logout.actions';
import { getBidderId, isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { EventContext } from '@/redux/modules/analytics';
import { getMyBidItemIds } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { getUnreadInboxCount } from '@/redux/modules/conversation/folders/conversationFolders.selectors';
import { getFollowedSellerIds } from '@/redux/modules/followSeller';
import { CompleteAccountModalContext, openCompleteAccountSignUpModal, openLoginModal } from '@/redux/modules/modal';
import { deleteRecentSearch, fetchRecentSearches, getHeaderRecentSearches } from '@/redux/modules/saveSearch';
import { fetchSellersIfNeeded, getHousesById } from '@/redux/modules/seller';
import { fetchIncompleteOrdersCountIfNeeded } from '@/redux/modules/wonItems/wonItems.actions';
import { getIncompleteOrderCount } from '@/redux/modules/wonItems/wonItems.selectors';
import { SEARCH_QUERY_MAX_LENGTH } from '@/types/search/searchQuery';
import { HeaderNavigationEventType, useTrackHeaderCategoryClickedAnalytics } from '@/utils/amplitude/analytics';
import {
    AnalyticsCTAEvent,
    useTrackCTAClickedAnalytics,
} from '@/utils/amplitude/analytics/cta/trackCTAClicked.analytics';
import {
    HowToWinLinkLocation,
    useTrackHowToWinClickedAnalytics,
} from '@/utils/amplitude/analytics/howToWin/trackHowToWinClicked.analytics';
import { useTrackHeaderNavigationClicked } from '@/utils/amplitude/analytics/navigation';
import { useSearchByImageExperiment } from '@/utils/amplitude/experiments/hooks/useSearchByImage.experiment';
import { useSearchInputRedesignExperiment } from '@/utils/amplitude/experiments/hooks/useSearchInputRedesignExperiment.experiment';
import { getItemIdFromPath } from '@/utils/urls';

import { EnableSmsOnSavePrompt } from './components/EnableSmsOnSavePrompt/EnableSmsOnSavePrompt';
import { SearchByImageInputAdornment } from './components/SearchByImageEntryPoint/SearchByImageInputAdornment';
import { SearchByImageTrailingButton } from './components/SearchByImageEntryPoint/SearchByImageTrailingButton';
import { useBrowserAlertsProps } from './hooks/useBrowserAlertsProps';
import { useCategoryNavigationAdsProps } from './hooks/useCategoryNavigationAdsProps';
import { useNotificationDataProps } from './hooks/useNotificationDataProps';
import { useSearchProps } from './hooks/useSearchProps';
import { useWhiteLabelProps } from './hooks/useWhiteLabelProps';

const MAXIMUM_WON_ITEMS_DISPLAY_COUNT = 10;
const MAX_SAVED_SELLERS_IN_SEARCH = 5;
const NAVIGATION_DROPDOWN_Z_INDEX_LAYER = 12;

export const HammerHeader = () => {
    const dispatch = useAppDispatch();
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const unreadCount = useAppSelector(getUnreadInboxCount);
    const incompleteOrdersCount = useAppSelector(getIncompleteOrderCount);
    const { upcomingBidItemIds = [] } = useAppSelector(getMyBidItemIds);
    const upcomingBidsCount = upcomingBidItemIds.length;
    const bidderId = useAppSelector(getBidderId);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const itemId = getItemIdFromPath(pathname);
    const followedSellerIds = useAppSelector(getFollowedSellerIds);
    const followedSellers = useAppSelector((state) => getHousesById(state, followedSellerIds));
    const trackHeaderCategoryClicked = useTrackHeaderCategoryClickedAnalytics();

    const onLogoutClicked = useCallback(() => dispatch(logOutBidder()), [dispatch]);

    const trackCTAClicked = useTrackCTAClickedAnalytics();
    const trackHeaderNavigationClicked = useTrackHeaderNavigationClicked();
    const trackHowToWinClicked = useTrackHowToWinClickedAnalytics();
    const { isShowingFeature: hasSearchInputRedesignExperiment, trackFeatureFlag: trackSearchInputRedesignExposure } =
        useSearchInputRedesignExperiment({ expose: false });
    // Resolve the Search by Image arm here, in the always-mounted header, so exposure fires
    // for every bucket incl. control — the arm-gated triggers below only mount for their own
    // arm, so they can't own the exposure call without control never firing.
    const { hasTreatmentA, hasTreatmentB } = useSearchByImageExperiment();

    const onLoginClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Login);
        dispatch(
            openLoginModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Login,
            })
        );
    }, [dispatch, itemId, trackCTAClicked]);

    const onSignUpClicked = useCallback(() => {
        trackCTAClicked(AnalyticsCTAEvent.HammerHeader.Join);
        dispatch(
            openCompleteAccountSignUpModal({
                eventContext: EventContext.join,
                id: itemId,
                itemId,
                type: CompleteAccountModalContext.Join,
            })
        );
    }, [dispatch, itemId, trackCTAClicked]);

    const onTrackCategoryClick = useCallback(
        (categoryName: string) => {
            trackHeaderCategoryClicked({ categoryName });
        },
        [trackHeaderCategoryClicked]
    );

    useEffect(() => {
        if (bidderId > 0) {
            dispatch(
                fetchIncompleteOrdersCountIfNeeded({
                    bidderId,
                    force: Boolean(bidderId),
                })
            );
        }
    }, [bidderId, dispatch]);

    useEffect(() => {
        if (followedSellerIds.length > 0) {
            dispatch(fetchSellersIfNeeded(followedSellerIds));
        }
    }, [dispatch, followedSellerIds]);

    useEffect(() => {
        if (hasSearchInputRedesignExperiment && bidderId > 0) {
            dispatch(fetchRecentSearches({}));
        }
    }, [bidderId, dispatch, hasSearchInputRedesignExperiment]);

    const searchProps = useSearchProps();
    // The app's intl (captured here, in the app tree). The hammer-ui Header mounts its
    // own IntlProvider with only hammer-ui messages, which shadows the app's for anything
    // rendered inside the header — the search-by-image triggers need the app intl re-provided.
    // useAppFormatMessage can't substitute: RawIntlProvider requires the full intl instance,
    // not just formatMessage.
    const appIntl = useIntl();
    const headerRecentSearches = useAppSelector(getHeaderRecentSearches);
    const recentSearchUrlByQuery = useMemo(
        () => new Map(headerRecentSearches.map(({ query, url }) => [query, url])),
        [headerRecentSearches]
    );

    const recentSearchHashByQuery = useMemo(
        () => new Map(headerRecentSearches.map(({ hash, query }) => [query, hash])),
        [headerRecentSearches]
    );

    const recentSearches = useMemo(() => headerRecentSearches.map(({ query }) => ({ query })), [headerRecentSearches]);

    const handleRecentSearchClick = useCallback(
        (recentSearch: RecentSearch) => {
            const recentSearchUrl = recentSearchUrlByQuery.get(recentSearch.query);

            if (recentSearchUrl) {
                navigate(recentSearchUrl, { state: { suggestionClicked: true } });
            }
        },
        [navigate, recentSearchUrlByQuery]
    );

    const handleRecentSearchDelete = useCallback(
        (recentSearch: RecentSearch) => {
            const hash = recentSearchHashByQuery.get(recentSearch.query);

            if (hash) {
                dispatch(deleteRecentSearch({ hash }));
            }
        },
        [dispatch, recentSearchHashByQuery]
    );

    const savedSellers = useMemo(
        (): SavedSeller[] =>
            followedSellers
                .slice(0, MAX_SAVED_SELLERS_IN_SEARCH)
                .flatMap((seller) => (seller?.name ? [{ sellerId: seller.sellerId, sellerName: seller.name }] : [])),
        [followedSellers]
    );

    const handleSavedSellerClick = useCallback(
        (savedSeller: SavedSeller) => {
            searchProps.handleSearch({
                houseId: savedSeller.sellerId,
                query: savedSeller.sellerName,
                suggestionClicked: true,
            });
        },
        [searchProps]
    );

    const handleSearchInputFocus = useCallback(() => {
        trackSearchInputRedesignExposure();
    }, [trackSearchInputRedesignExposure]);

    const headerProps: HeaderProps = {
        browserAlerts: useBrowserAlertsProps(),
        callbacks: {
            onAlertsClicked: () => {
                trackHeaderNavigationClicked({
                    eventType: HeaderNavigationEventType.Alerts,
                });
            },
            onHowToWinClicked: () => {
                trackHowToWinClicked(HowToWinLinkLocation.NavHeader);
            },
            onMyItemsClicked: () => {
                trackHeaderNavigationClicked({
                    eventType: HeaderNavigationEventType.MyItems,
                });
            },
            onWonItemsClicked: () => {
                trackHeaderNavigationClicked({
                    eventType: HeaderNavigationEventType.WonItems,
                });
            },
        },
        categoryNavigationAds: useCategoryNavigationAdsProps(),
        intl: en,
        isUserLoggedIn: userLoggedIn,
        notificationData: useNotificationDataProps(),
        onLoginClicked,
        onLogoutClicked,
        onSignUpClicked,
        search: {
            ...searchProps,
            endAdornment: hasTreatmentB ? (
                <RawIntlProvider value={appIntl}>
                    <SearchByImageInputAdornment />
                </RawIntlProvider>
            ) : undefined,
            maxLength: SEARCH_QUERY_MAX_LENGTH,
            trailingContent: hasTreatmentA ? (
                <RawIntlProvider value={appIntl}>
                    <SearchByImageTrailingButton />
                </RawIntlProvider>
            ) : undefined,
        },
        searchInputRedesignExperiment: {
            enabled: hasSearchInputRedesignExperiment,
            handleRecentSearchClick,
            handleRecentSearchDelete,
            handleSavedSellerClick,
            handleSearchInputFocus,
            recentSearches,
            savedSellers,
            showQueryEcho: searchProps.hasNoSuggestionsForQuery,
        },
        trackCategoryClick: onTrackCategoryClick,
        unreadCount,
        upcomingBidsCount,
        wonItemsCount: incompleteOrdersCount,
        wonItemsMaximumCount: MAXIMUM_WON_ITEMS_DISPLAY_COUNT,
        ...useWhiteLabelProps(),
        zIndexes: {
            megaNavDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
            searchDropdown: NAVIGATION_DROPDOWN_Z_INDEX_LAYER,
        },
    };

    // Key by search queryText so the header search input remounts when URL keyword changes (e.g. pill click) and shows the new value
    const headerKey = `search-${headerProps.search.queryText ?? ''}`;

    return (
        <>
            <Header
                key={headerKey}
                {...headerProps}
            />
            <EnableSmsOnSavePrompt />
        </>
    );
};
